import React, { useMemo } from "react";
import { Column, usePagination, useSortBy, useTable } from "react-table";
import { ObjectPanelTableProps } from "../../../../types/ObjectBrowser/ComponentTypes";
import ObjectDetailsType from "../../../../types/ObjectBrowser/ObjectDetailsType";

// Components
import NoData from "../../../../component/NoData";
import './style.scss';

const ObjectPanelTable: React.FC<ObjectPanelTableProps> = ({ instance, selectedItem, optionsList, loading, setSelectedObject }) => {
    const objectType = selectedItem?.objectType;

    const onRowClick = (row: any) => {
        setSelectedObject({
            ...row,
            instanceId: instance.id,
        });
    };

    const filteredData = useMemo(() => {
        return optionsList.map((row: any) => {
            const type = row.type ?? 'Table'; // fallback if `type` is missing

            if (type === 'Table') {
                return {
                    tablename: row.tablename,
                    tableschema: row.tableschema,
                    database: row.database,
                    objectType: type,
                    rows: row.rows,
                    tablesizebytes: row.tablesizebytes,
                    indexsizebytes: row.indexsizebytes,
                } as ObjectDetailsType;
            } else {
                return {
                    tablename: row.tablename,
                    indexname: row.indexname,
                    tableschema: row.indexschema,
                    indexschema: row.indexschema,
                    database: row.database,
                    objectType: type,
                    rows: null,
                    tablesizebytes: row.tablesizebytes,
                    indexsizebytes: row.indexsizebytes,
                } as ObjectDetailsType;
            }
        });
    }, [optionsList]);


    const columns = useMemo<Column[]>(() => [
        {
            Header: objectType === "Table" ? "Table" : "Index",
            accessor: objectType === "Table" ? 'tablename' : 'indexname',
            Cell: ({ row, value }: { row: any, value: string }) => (
                    <>
                        {row.original.objectType === "Table" && <i className="fa fa-table me-1" aria-hidden="true"/>}
                        {row.original.objectType === "Index" && <i className="fa fa-list-ol me-1" aria-hidden="true"/>}
                        <span className='panel-link' onClick={() => onRowClick(row.original)}>
                            {value}
                    </span>
                    </>
            ),
        },
        {
            Header: "Schema",
            accessor: "tableschema",
        },
        {
            Header: "Database",
            accessor: "database",
        },
        ...(objectType === "Table" ? [
            {
                Header: "Rows",
                accessor: "rows",
                Cell: ({ value }: { value: number | null }) => value?.toLocaleString() ?? "-",
            },
        ] : []),
        {
            Header: "Size",
            accessor: "tablesizebytes",
            Cell: ({ value }: { value: number | undefined }) => value ? `${(value / 1024 / 1024).toFixed(2)} MB` : "-",
        },
    ], [objectType, optionsList]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page
    } = useTable(
            {
                columns,
                data: filteredData,
                initialState: { pageIndex: 0, pageSize: 10 },
            },
            useSortBy,
            usePagination
    );

    return (
            <div className="table-responsive">
                <table {...getTableProps()} className="table table-hover">
                    <thead>
                    {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps()}>
                                            {column.render('Header')}
                                        </th>
                                ))}
                            </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {page.map(row => {
                        prepareRow(row);
                        return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                            <td {...cell.getCellProps()}>
                                                {cell.render('Cell')}
                                            </td>
                                    ))}
                                </tr>
                        );
                    })}
                    </tbody>
                </table>
                <NoData error={null} loading={loading} length={filteredData.length}/>
            </div>
    );
};

export default ObjectPanelTable;
